export type ImportWorkflowTaskStatus =
  "uploading" | "processing" | "complete" | "error";

export interface ImportWorkflowTask {
  id: string;
  filename: string;
  recordingId?: string;
  studyId?: string;
  patientId?: string;
  status: ImportWorkflowTaskStatus;
  progress: number;
  stage: string;
  error?: string;
  startedAt: number;
  updatedAt: number;
  completedAt?: number;
}

export interface ImportCompletionPayload {
  patientId: string;
  studyId: string;
  recordingId: string;
  videoId?: string | null;
}

export interface RecordingStatusPayload {
  data?: RecordingStatusPayload;
  recording_id?: string;
  filename?: string;
  processing_status?: string | null;
  status?: string | null;
  progress?: number | null;
  completed_at?: string | null;
  error?: string | null;
  logs?: Array<{
    operation_status?: string | null;
    error_message?: string | null;
    completed_at?: string | null;
  }>;
}

const IMPORT_STATUS_COMPLETE = new Set([
  "complete",
  "completed",
  "ready",
  "success",
  "succeeded",
]);

const IMPORT_STATUS_ERROR = new Set([
  "artifact_missing",
  "error",
  "failed",
  "failure",
]);

export const IMPORT_STATUS_LABELS: Record<ImportWorkflowTaskStatus, string> = {
  uploading: "Uploading",
  processing: "Preprocessing",
  complete: "Ready",
  error: "Needs attention",
};

export type ImportStatusVariant =
  "info" | "warning" | "danger" | "primary" | "success";

export const IMPORT_STATUS_VARIANTS: Record<
  ImportWorkflowTaskStatus,
  ImportStatusVariant
> = {
  uploading: "info",
  processing: "warning",
  complete: "success",
  error: "danger",
};

export const EEG_IMPORT_TASKS_STORAGE_KEY = "eeg_import_tasks_v1";

const normalizeImportStatusToken = (value: unknown): string =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

export const clampProgress = (value: number): number =>
  Math.max(0, Math.min(100, Math.round(value)));

export const deriveImportTaskProgress = (
  rawStatus: unknown,
  reportedProgress: unknown,
  previousProgress: number,
): Pick<ImportWorkflowTask, "status" | "progress" | "stage"> => {
  const token = normalizeImportStatusToken(rawStatus);
  const numericProgress =
    typeof reportedProgress === "number" && Number.isFinite(reportedProgress)
      ? reportedProgress
      : null;
  if (IMPORT_STATUS_COMPLETE.has(token)) {
    return { status: "complete", progress: 100, stage: "Ready for review" };
  }
  if (IMPORT_STATUS_ERROR.has(token)) {
    return { status: "error", progress: 100, stage: "Preprocessing failed" };
  }
  if (numericProgress !== null && numericProgress > 0) {
    return {
      status: "processing",
      progress: clampProgress(Math.max(previousProgress, numericProgress)),
      stage:
        token === "queued" ? "Queued for preprocessing" : "Preprocessing EEG",
    };
  }
  if (token === "queued" || token === "pending") {
    return {
      status: "processing",
      progress: Math.max(previousProgress, 72),
      stage: "Queued for preprocessing",
    };
  }
  if (token === "processing") {
    return {
      status: "processing",
      progress: Math.max(previousProgress, 88),
      stage: "Processing EEG",
    };
  }
  if (token === "preprocessing") {
    return {
      status: "processing",
      progress: Math.max(previousProgress, 82),
      stage: "Preprocessing EEG",
    };
  }
  return {
    status: "processing",
    progress: Math.max(previousProgress, 75),
    stage: "Checking preprocessing status",
  };
};

export function loadPersistedImportTasks(): ImportWorkflowTask[] {
  if (typeof window === "undefined") return [];
  try {
    const raw =
      window.localStorage.getItem(EEG_IMPORT_TASKS_STORAGE_KEY) ||
      window.sessionStorage.getItem(EEG_IMPORT_TASKS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ImportWorkflowTask[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function persistImportTasks(tasks: ImportWorkflowTask[]): void {
  if (typeof window === "undefined") return;
  try {
    const nonTerminal = tasks.filter(
      (task) => task.status === "uploading" || task.status === "processing",
    );
    const recentTerminal = tasks
      .filter((task) => task.status === "complete" || task.status === "error")
      .slice(-10);
    window.sessionStorage.setItem(
      EEG_IMPORT_TASKS_STORAGE_KEY,
      JSON.stringify([...nonTerminal, ...recentTerminal]),
    );
    window.localStorage.setItem(
      EEG_IMPORT_TASKS_STORAGE_KEY,
      JSON.stringify([...nonTerminal, ...recentTerminal]),
    );
  } catch {
    // Ignore storage quota or privacy mode failures.
  }
}

export type ImportActivityStatus = "idle" | "active" | "failed";

export type ImportActivitySummary = {
  status: ImportActivityStatus;
  activeCount: number;
  failedCount: number;
  completeCount: number;
  activeTasks: Array<{
    id: string;
    filename: string;
    status: ImportWorkflowTaskStatus;
    progress: number;
    stage: string;
    error?: string;
  }>;
  failedTasks: Array<{
    filename: string;
    error?: string;
    stage?: string;
  }>;
};

export function summarizeImportActivity(
  importTasks: ImportWorkflowTask[],
  isImporting: boolean,
): ImportActivitySummary {
  const activeTasks = importTasks.filter(
    (task) => task.status === "uploading" || task.status === "processing",
  );
  const activeCount =
    activeTasks.length > 0 ? activeTasks.length : isImporting ? 1 : 0;
  const failedTasks = importTasks
    .filter((task) => task.status === "error")
    .map((task) => ({
      filename: task.filename,
      error: task.error,
      stage: task.stage,
    }));
  const completeCount = importTasks.filter(
    (task) => task.status === "complete",
  ).length;

  let status: ImportActivityStatus = "idle";
  if (failedTasks.length > 0) {
    status = "failed";
  } else if (activeCount > 0) {
    status = "active";
  }

  return {
    status,
    activeCount,
    failedCount: failedTasks.length,
    completeCount,
    activeTasks: activeTasks.map((task) => ({
      id: task.id,
      filename: task.filename,
      status: task.status,
      progress: task.progress,
      stage: task.stage,
      error: task.error,
    })),
    failedTasks,
  };
}
